import {
  APPROVAL_FILTER_FIELDS,
  APPROVAL_SORT_PAGINATION_FIELDS,
  APPROVAL_STATUS,
} from 'src/services/consts/approval.const'
import { CONFIRMATION_NOT_REQUIRED_YEARS } from 'src/services/consts/confirmations.const'
import {
  formatWebsiteUrl,
  getCutoffDate,
  getFilterFromQuery,
  toUtcDay,
} from 'src/services/app/util.service'
import { idForCustomEntry, resolveCustomWebsites } from 'src/services/helpers/ticket.helper'

export function filterApprovals(approvals, filterBy) {
  if (!approvals) return []
  const approvalsToFilter = [...approvals]

  const searchTerm = filterBy.search ? filterBy.search.toLowerCase() : null
  const createdCutoffDate = getCutoffDate(filterBy.dateCreatedRange, false)
  const lastApprovedCutoffDate = getCutoffDate(filterBy.lastApprovedRange, false)
  // Only apply status filter if explicitly provided (not null/undefined)
  const status = filterBy.status !== null && filterBy.status !== undefined ? filterBy.status : null

  return approvalsToFilter.filter((approval) => {
    const matchesSearch =
      !searchTerm ||
      approval.companyName?.toLowerCase().includes(searchTerm) ||
      approval.productName?.toLowerCase().includes(searchTerm)

    const matchesdateCreated =
      !createdCutoffDate || new Date(approval.dateCreated) >= createdCutoffDate
    const matchesLastApprovedDate =
      !lastApprovedCutoffDate ||
      new Date(approval.lastApproved || approval.dateCreated) >= lastApprovedCutoffDate

    // Status filter condition - only apply if status is explicitly set
    let matchesStatus = true
    if (status !== null) {
      if (status === 'active') {
        matchesStatus = approval.active
      } else if (status === 'inactive') {
        // Revoked: must not be active
        matchesStatus = !approval.active
      }
    }

    return matchesSearch && matchesdateCreated && matchesLastApprovedDate && matchesStatus
  })
}

export function getDirectoryCounts(approvals) {
  // Single pass through all approvals to count by directory
  return approvals.reduce(
    (counts, approval) => {
      if (approval.active) {
        counts[APPROVAL_STATUS.active]++
      } else {
        counts[APPROVAL_STATUS.inactive]++
      }
      return counts
    },
    {
      [APPROVAL_STATUS.active]: 0,
      [APPROVAL_STATUS.inactive]: 0,
    }
  )
}

export function getDefaultFilter() {
  return {
    ...APPROVAL_FILTER_FIELDS,
  }
}

export function getDefaultSort() {
  return {
    ...APPROVAL_SORT_PAGINATION_FIELDS,
  }
}

// A request folder such as drafts is not an approvals directory.
export function hasUnknownApprovalStatus(query) {
  const status = query?.status
  if (!status) return false
  return status !== APPROVAL_STATUS.active && status !== APPROVAL_STATUS.inactive
}

export function getApprovalFilterFromQuery(query) {
  const filter = getFilterFromQuery(getDefaultFilter(), query)
  if (hasUnknownApprovalStatus(query)) {
    filter.status = APPROVAL_STATUS.active
  }
  return filter
}

// Helper functions
export function getApprovalActiveIcon(active) {
  if (active === true) return 'badge-check'
  else if (active === false) return 'badge-x'
  return 'circle-x'
}

export function getApprovalStatusText(active) {
  if (active === true) return 'Active'
  if (active === false) return 'Revoked'
  return 'N/A'
}

export function isConfirmationNotRequired(approval) {
  if (!approval?.nextUserConfirmation) return false

  const nextDue = new Date(approval.nextUserConfirmation)
  const threshold = new Date()
  threshold.setFullYear(threshold.getFullYear() + CONFIRMATION_NOT_REQUIRED_YEARS)
  return nextDue > threshold
}

// Reactive callers pass `today` from useUtcToday() so they re-run when the day changes.
export function isApprovalOverdue(approval, today = Date.now()) {
  if (!approval?.active || !approval?.nextUserConfirmation) return false
  if (isConfirmationNotRequired(approval)) return false

  return toUtcDay(approval.nextUserConfirmation) <= toUtcDay(today)
}

// Refactor detailed approval from get_user_auth endpoint
export function refactorApproval(rawApproval) {
  return {
    approvalId: rawApproval.user_auth_id,
    active: rawApproval.active,
    approvalConditions: rawApproval.auth_conditions || null,
    dateCreated: rawApproval.created,
    productId: idForCustomEntry(rawApproval.product_id, rawApproval.product_nf_info),
    productName: rawApproval.product_name || rawApproval.product_nf_info?.product_nf_name || null,
    productWebsite:
      rawApproval.product_nf_info?.product_nf_website || rawApproval.product_website || null,
    companyId: idForCustomEntry(rawApproval.company_id, rawApproval.company_nf_info),
    companyName: rawApproval.company_name || rawApproval.company_nf_info?.company_nf_name || null,
    companyWebsite:
      rawApproval.company_nf_info?.company_nf_website || rawApproval.company_website || null,
    ticketId: rawApproval.ticket_id,
    userId: rawApproval.user_id,
    approvedBy: rawApproval.approved_by,
    lastUserConfirmation: rawApproval.last_user_audit,
    nextUserConfirmation: rawApproval.next_user_audit,
    // Revocation info (only present if revoked)
    revokedBy: rawApproval.revoked_by || null,
    revokedOn: rawApproval.revoked_on || null,
    revokeReason: rawApproval.revoked_reason || null,
  }
}

// Refactor mini approval format (from confirmation approval response)
export function refactorMiniApproval(rawApproval) {
  return {
    approvalId: rawApproval.user_auth_id,
    companyId: idForCustomEntry(rawApproval.company_id, rawApproval.company_nf_info),
    productId: idForCustomEntry(rawApproval.product_id, rawApproval.product_nf_info),
    ticketId: rawApproval.ticket_id,
    active: rawApproval.active,
    dateCreated: rawApproval.created,
    companyName: rawApproval.company_name || rawApproval.company_nf_info?.company_nf_name || null,
    productName: rawApproval.product_name || rawApproval.product_nf_info?.product_nf_name || null,
    companyWebsite:
      rawApproval.company_nf_info?.company_nf_website || rawApproval.company_website || null,
    productWebsite:
      rawApproval.product_nf_info?.product_nf_website || rawApproval.product_website || null,
    lastUserConfirmation: rawApproval.last_user_audit,
    nextUserConfirmation: rawApproval.next_user_audit,
  }
}

// Enrich a detailed approval with directory names/websites.
// nextUserConfirmation comes from the account mini approval when the detail omits it.
export function enrichDetailedApproval(detailedApproval, miniApprovalsArray, companyDictionary) {
  if (!detailedApproval) return null

  // Find the matching mini approval from the array
  const miniApproval = miniApprovalsArray.find((a) => a.approvalId === detailedApproval.approvalId)

  const companyName = companyDictionary.resolveCompanyName({
    companyId: detailedApproval.companyId,
    fallbackName: detailedApproval.companyName,
  })

  const productName = companyDictionary.resolveProductName({
    companyId: detailedApproval.companyId,
    productId: detailedApproval.productId,
    fallbackName: detailedApproval.productName,
  })

  const { isCustomCompany, isCustomProduct, companyWebsite, productWebsite } =
    resolveCustomWebsites(detailedApproval)
  const website = formatWebsiteUrl(
    isCustomCompany ? companyWebsite : companyDictionary.getWebsite(detailedApproval.companyId)
  )
  const displayProductWebsite = formatWebsiteUrl(
    isCustomProduct ? productWebsite : detailedApproval.productWebsite
  )

  return {
    ...detailedApproval,
    nextUserConfirmation:
      detailedApproval.nextUserConfirmation ?? miniApproval?.nextUserConfirmation ?? null,
    // Add company/product info
    companyName,
    productName,
    website,
    productWebsite: displayProductWebsite,
  }
}
